import { useEffect, useRef, useState } from 'react';

import { PropertyViewIcon, ShieldUserIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import patternValidators from 'helpers/validators/pattern-validators';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { CodeInput } from 'components/CodeInput/CodeInput';
import { Button, FormMessage, Input, InputGroup } from 'components/FormComponents';
import { MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import { Loading } from 'components/Loading/Loading';

import {
  useDecodeInvitationQuery,
  useLoginWithPasswordMutation,
  useRequestOtpMutation,
  useSelectContextMutation,
  useVerifyOtpMutation,
} from 'api/candidate-portal-api';
import {
  selectInvitationContext,
  setAuthenticated,
  setInvitationContext,
  setSelectedContext,
} from 'api/candidate-portal-slice';

const LOGIN_STEPS = {
  EMAIL: 'email',
  OTP: 'otp',
  PASSWORD: 'password',
};

const CandidatePortalLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useParams();
  const { t } = useTranslation();

  const [step, setStep] = useState(LOGIN_STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [messageText, setMessageText] = useState(null);
  const [messageType, setMessageType] = useState(MessageType.Error);
  const [countdown, setCountdown] = useState(0);
  const [otpInputKey, setOtpInputKey] = useState(0);

  // Refs for focus management
  const emailStepRef = useRef(null);
  const otpStepRef = useRef(null);
  const passwordStepRef = useRef(null);
  const mainContentRef = useRef(null);

  const invitationContext = useSelector(selectInvitationContext);
  const [selectContext, { isLoading: isSelectingContext }] = useSelectContextMutation();

  // Decode invitation token if provided
  const { data: invitation, isLoading: isLoadingInvitation } = useDecodeInvitationQuery(token, {
    skip: !token,
  });

  const [requestOtp, { isLoading: isRequestingOtp }] = useRequestOtpMutation();
  const [verifyOtp, { isLoading: isVerifyingOtp }] = useVerifyOtpMutation();
  const [loginWithPassword, { isLoading: isLoggingIn }] = useLoginWithPasswordMutation();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  // Handle invitation context
  useEffect(() => {
    if (invitation) {
      dispatch(setInvitationContext(invitation));
      setEmail(invitation.email);
      setValue('email', invitation.email);
    }
  }, [invitation, dispatch, setValue]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Focus management when step changes
  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      if (step === LOGIN_STEPS.EMAIL && emailStepRef.current) {
        const emailInput = emailStepRef.current.querySelector('input[name="email"]');
        if (emailInput && !invitationContext?.email) {
          emailInput.focus();
        }
      } else if (step === LOGIN_STEPS.OTP && otpStepRef.current) {
        const codeInput = otpStepRef.current.querySelector('input');
        if (codeInput) {
          codeInput.focus();
        }
      } else if (step === LOGIN_STEPS.PASSWORD && passwordStepRef.current) {
        const passwordInput = passwordStepRef.current.querySelector('input[name="password"]');
        if (passwordInput) {
          passwordInput.focus();
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [step, invitationContext, otpInputKey]);

  const showMessage = (text, type = MessageType.Error) => {
    setMessageText(text);
    setMessageType(type);
  };

  const handleAuthenticationResult = async result => {
    dispatch(
      setAuthenticated({
        candidateAuthId: result.candidate_auth_id,
        email: result.email,
        hasPassword: result.has_password,
        linkedCandidates: result.linked_candidates,
        selectedCandidateId: result.selected_candidate_id,
        selectedCompanyId: result.selected_company_id,
      }),
    );

    if (result.linked_candidates?.length > 1 && !result.selected_candidate_id) {
      navigate('/portal/select');
    } else if (result.linked_candidates?.length === 0) {
      showMessage(t('candidatePortal.noBackgroundChecksFound'));
    } else {
      const candidateId =
        result.selected_candidate_id || result.linked_candidates?.[0]?.candidate_id;

      if (!candidateId) {
        showMessage(t('candidatePortal.unableToDetermineCandidateContext'));
        return;
      }

      const data = await selectContext({
        candidateId,
      }).unwrap();

      dispatch(
        setSelectedContext({
          candidateId: data.candidate_id,
          companyId: data.company_id,
        }),
      );
      navigate(`/portal/dashboard/${candidateId}`);
    }
  };

  const handleRequestOtp = async data => {
    try {
      const result = await requestOtp({
        email: data.email,
        candidateId: invitationContext?.candidate_id,
        companyId: invitationContext?.company_id,
      }).unwrap();

      setEmail(data.email);
      setHasPassword(result.has_password);
      setStep(LOGIN_STEPS.OTP);
      setCountdown(60); // 60 second cooldown before resend
      showMessage(t('candidatePortal.verificationCodeSentToYourEmail'), MessageType.Info);
      // Announce to screen readers
      if (mainContentRef.current) {
        const announcement = document.createElement('div');
        announcement.setAttribute('role', 'status');
        announcement.setAttribute('aria-live', 'polite');
        announcement.setAttribute('aria-atomic', 'true');
        announcement.className = 'sr-only';
        announcement.textContent = t('candidatePortal.verificationCodeSentToYourEmail');
        mainContentRef.current.appendChild(announcement);
        setTimeout(() => announcement.remove(), 1000);
      }
    } catch (error) {
      showMessage(error?.data?.error?.log || t('candidatePortal.failedToSendVerificationCode'));
    }
  };

  const handleVerifyOtp = async code => {
    try {
      const result = await verifyOtp({
        email,
        otp: code,
        candidateId: invitationContext?.candidate_id,
        companyId: invitationContext?.company_id,
      }).unwrap();

      await handleAuthenticationResult(result);
    } catch (error) {
      showMessage(error?.data?.error?.log || t('candidatePortal.invalidVerificationCode'));
      setOtpInputKey(k => k + 1);
    }
  };

  const handlePasswordLogin = async data => {
    try {
      const result = await loginWithPassword({
        email,
        password: data.password,
        candidateId: invitationContext?.candidate_id,
        companyId: invitationContext?.company_id,
      }).unwrap();

      await handleAuthenticationResult(result);
    } catch (error) {
      showMessage(error?.data?.error?.log || t('candidatePortal.invalidCredentials'));
    }
  };

  const handleResendOtp = () => {
    handleRequestOtp({ email });
  };

  const handleUsePassword = () => {
    setStep(LOGIN_STEPS.PASSWORD);
    setMessageText(null);
  };

  const handleBackToOtp = () => {
    setStep(LOGIN_STEPS.OTP);
    setMessageText(null);
  };

  const isLoading =
    isLoadingInvitation || isRequestingOtp || isVerifyingOtp || isLoggingIn || isSelectingContext;

  // Get brand styling from invitation context
  const brandStyle = invitationContext?.brand
    ? {
        '--brand-button-bg': invitationContext.brand.color_button,
        '--brand-button-text': invitationContext.brand.color_button_text,
        '--brand-link-color': invitationContext.brand.color_link,
      }
    : {};

  // Button className with brand colors from brandStyle or defaults
  const buttonClassName = invitationContext?.brand?.color_button
    ? 'w-full bg-[var(--brand-button-bg)] text-[var(--brand-button-text)] hover:!bg-[var(--brand-button-bg)]'
    : 'w-full bg-background-gradient text-white';

  const backgroundColorClass = invitationContext?.brand?.color_button
    ? 'bg-[var(--brand-button-bg)]'
    : 'bg-surface-secondary';
  const leftBackgroundColorClass = invitationContext?.brand?.color_button
    ? 'bg-[var(--brand-button-bg)]'
    : 'bg-brand-gradient';

  const linkClassName = invitationContext?.brand?.color_link
    ? 'text-[var(--brand-link-color)]'
    : '';

  const displayClassName = invitationContext?.brand?.color_button_text
    ? 'text-[var(--brand-button-text)]'
    : 'text-white/80';

  return (
    <div className={`md:bg-none ${backgroundColorClass} min-h-screen flex`} style={brandStyle}>
      <Loading active={isLoading} />
      {/* ARIA live region for status announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        aria-label={t('candidatePortal.statusUpdates')}>
        {messageText && messageType === MessageType.Info ? messageText : ''}
      </div>
      {/* ARIA live region for error announcements */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
        aria-label={t('candidatePortal.errorMessages')}>
        {messageText && messageType === MessageType.Error ? messageText : ''}
      </div>
      {/* Loading state announcement */}
      {isLoading && (
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {t('candidatePortal.loading')}
        </div>
      )}
      <aside
        className={`hidden-sm hidden-md hidden-xs lg:flex lg:w-1/2 ${leftBackgroundColorClass} relative overflow-hidden`}
        aria-label={t('candidatePortal.featureInformation')}>
        <div
          className="absolute inset-0 object-cover bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50"
          aria-hidden="true"
        />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <img
            width={120}
            src="/assets/images/checkmate/label.png"
            alt={t('candidatePortal.checkmateLogo')}
          />

          <div className="flex flex-col gap-4">
            <HugeiconsIcon
              icon={PropertyViewIcon}
              className={`${displayClassName}`}
              size="64"
              aria-hidden="true"
            />
            <h1 className={`text-5xl font-semibold leading-tight ${displayClassName}`}>
              {t('candidatePortal.trackAllYourBackgroundChecksWithEase')}
            </h1>
            <p className={`text-2xl ${displayClassName}`}>
              {t('candidatePortal.checkProgressViewConsentsAndDownloadReportsAllInOnePlace')}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <HugeiconsIcon
              icon={ShieldUserIcon}
              className={`${displayClassName}`}
              size="20"
              aria-hidden="true"
            />
            <span className={`text-xl font-light ${displayClassName}`}>
              {t('candidatePortal.yourDataIsProtectedWithEnterpriseGradeSecurity')}
            </span>
          </div>
        </div>
      </aside>

      <main
        className="flex-1 flex flex-col p-6 lg:p-12 checkmate-logo-background"
        style={brandStyle}
        ref={mainContentRef}>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-3xl">
            <div className="rounded-2xl bg-white shadow-sm shadow-soft border-0 p-12 space-y-12">
              <div className="w-40">
                <img
                  src={
                    invitationContext?.brand?.logo_url
                      ? invitationContext?.brand?.logo_url
                      : '/assets/images/checkmate/icon-label.svg'
                  }
                  alt={
                    invitationContext?.brand?.logo_url
                      ? t('candidatePortal.clientBrandLogo', {
                          company: invitationContext?.company_name || '',
                        })
                      : t('candidatePortal.checkmateLogo')
                  }
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="space-y-8">
                <header className="flex flex-col gap-2">
                  <h2 className="text-4xl font-medium text-text-primary">
                    {step === 'email'
                      ? t('candidatePortal.candidatePortal')
                      : t('candidatePortal.enterVerificationCode')}
                  </h2>
                  <p className="text-text-secondary font-light">
                    {step === 'email'
                      ? email
                        ? `${t('candidatePortal.signInToViewYourBackgroundChecksProgressAndReports')} ${invitationContext?.company_name ? `for ${invitationContext.company_name}` : ''}`
                        : `${t('candidatePortal.enterTheEmailAddressLinkedToYourBackgroundChecksToViewTheirProgressAndReports')}`
                      : `${t('candidatePortal.enterThe6DigitCodeSentToYourEmail')}`}
                  </p>
                </header>

                {/* Email Step */}
                {step === LOGIN_STEPS.EMAIL && (
                  <form
                    onSubmit={handleSubmit(handleRequestOtp)}
                    className="w-full space-y-12"
                    ref={emailStepRef}
                    aria-label={t('candidatePortal.emailLoginForm')}
                    noValidate>
                    <div className="space-y-2">
                      <InputGroup title={t('candidatePortal.emailAddress')}>
                        <Input
                          name="email"
                          icon="mail"
                          placeholder={t('candidatePortal.enterYourEmail')}
                          register={register}
                          validators={{ required: true, ...patternValidators.email }}
                          error={
                            errors?.email?.message ||
                            (errors?.email?.type === 'required'
                              ? t('candidatePortal.emailIsRequired')
                              : errors?.email?.type)
                          }
                          disabled={!!invitationContext?.email}
                          autoComplete="email"
                          inputProps={{
                            'aria-required': 'true',
                            'aria-invalid': errors?.email ? 'true' : 'false',
                            'aria-describedby': errors?.email ? 'email-error' : 'email-description',
                          }}
                        />
                      </InputGroup>
                      {errors?.email && (
                        <div id="email-error" role="alert" className="sr-only">
                          {errors.email.message || t('candidatePortal.emailIsRequired')}
                        </div>
                      )}
                      {email && (
                        <p
                          id="email-description"
                          className="text-text-secondary font-light text-lg"
                          role="status">
                          {t('candidatePortal.thisEmailWasProvidedByYourEmployer')}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <Button
                        submit
                        className={buttonClassName}
                        aria-label={t('candidatePortal.sendLoginCode')}>
                        {t('candidatePortal.sendLoginCode')}
                      </Button>
                      <p className="text-text-secondary font-light text-lg">
                        {t('candidatePortal.aOneTimeLoginCodeWillBeSentToYourEmail')}
                      </p>
                    </div>
                  </form>
                )}

                {/* OTP Step */}
                {step === LOGIN_STEPS.OTP && (
                  <div
                    className="w-full space-y-6 flex flex-col"
                    ref={otpStepRef}
                    role="region"
                    aria-label={t('candidatePortal.verificationCodeSection')}>
                    <div className="flex flex-col gap-4">
                      <div>
                        <label htmlFor="otp-input" className="sr-only">
                          {t('candidatePortal.enterThe6DigitCodeSentToYourEmail')}
                        </label>
                        <CodeInput
                          key={otpInputKey}
                          onComplete={handleVerifyOtp}
                          length={6}
                          className="w-full"
                          id="otp-input"
                          aria-label={t('candidatePortal.verificationCodeInput')}
                        />
                      </div>

                      <div
                        role="alert"
                        aria-live={messageType === MessageType.Error ? 'assertive' : 'polite'}>
                        <FormMessage
                          message={messageText}
                          type={messageType}
                          onClose={() => setMessageText(null)}
                        />
                      </div>
                    </div>

                    <nav aria-label={t('candidatePortal.loginOptions')}>
                      {countdown > 0 ? (
                        <p
                          className={`${linkClassName} font-light`}
                          role="status"
                          aria-live="polite">
                          {t('candidatePortal.didNotReceiveTheCode')}{' '}
                          <span
                            aria-label={t('candidatePortal.countdownSeconds', {
                              seconds: countdown,
                            })}>
                            {countdown}s
                          </span>
                        </p>
                      ) : (
                        <Button
                          type="link"
                          onClick={handleResendOtp}
                          className={linkClassName}
                          aria-label={t('candidatePortal.resendVerificationCode')}>
                          {t('candidatePortal.resendCode')}
                        </Button>
                      )}

                      {hasPassword && (
                        <Button
                          type="link"
                          onClick={handleUsePassword}
                          className={linkClassName}
                          aria-label={t('candidatePortal.switchToPasswordLogin')}>
                          {t('candidatePortal.usePasswordInstead')}
                        </Button>
                      )}
                    </nav>
                  </div>
                )}

                {step === LOGIN_STEPS.PASSWORD && (
                  <form
                    onSubmit={handleSubmit(handlePasswordLogin)}
                    className="w-full space-y-6"
                    ref={passwordStepRef}
                    aria-label={t('candidatePortal.passwordLoginForm')}
                    noValidate>
                    <p className="text-text-secondary font-light">
                      {t('candidatePortal.signingInAs')}{' '}
                      <span className="text-text-primary font-medium" aria-label={email}>
                        {email}
                      </span>
                    </p>

                    <div className="space-y-2">
                      <InputGroup title={t('candidatePortal.password')}>
                        <Input
                          name="password"
                          icon="lock"
                          type="password"
                          placeholder={t('candidatePortal.enterYourPassword')}
                          register={register}
                          validators={{ required: true }}
                          error={errors?.password?.type}
                          autoComplete="current-password"
                          inputProps={{
                            'aria-required': 'true',
                            'aria-invalid': errors?.password ? 'true' : 'false',
                            'aria-describedby': errors?.password ? 'password-error' : undefined,
                          }}
                        />
                      </InputGroup>
                      {errors?.password && (
                        <div id="password-error" role="alert" className="sr-only">
                          {t('candidatePortal.passwordIsRequired')}
                        </div>
                      )}

                      <div
                        role="alert"
                        aria-live={messageType === MessageType.Error ? 'assertive' : 'polite'}>
                        <FormMessage
                          message={messageText}
                          type={messageType}
                          className="u-margin-top--0"
                          onClose={() => setMessageText(null)}
                        />
                      </div>
                    </div>

                    <Button
                      submit
                      className={buttonClassName}
                      aria-label={t('candidatePortal.signInWithPassword')}>
                      {t('candidatePortal.signIn')}
                    </Button>

                    <Button
                      type="link"
                      onClick={handleBackToOtp}
                      className={linkClassName}
                      aria-label={t('candidatePortal.switchToVerificationCode')}>
                      {t('candidatePortal.useVerificationCodeInstead')}
                    </Button>
                  </form>
                )}
              </div>

              <footer className="text-text-secondary font-light text-center flex items-center justify-center gap-2 w-full text-lg">
                {t('candidatePortal.poweredBy')}{' '}
                <a
                  href="https://checkmate.tech"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-primary font-medium"
                  aria-label={t('candidatePortal.visitCheckmateWebsite')}>
                  <img
                    src="/assets/images/checkmate/icon-label.svg"
                    alt={t('candidatePortal.checkmateLogo')}
                    className="w-32"
                  />
                </a>
              </footer>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CandidatePortalLogin;
